import { useEffect, useId, useRef, useState } from "react";
import { type City, citiesApi } from "../lib/api/cities.js";
import { flag } from "../lib/flag.js";
import "./CityAutocomplete.css";

/** Reusable city picker: debounced search, keyboard-navigable, with loading and
 *  no-match feedback. Returns the full City on select (name/country/tz/coords). */
export function CityAutocomplete({
  label,
  initial,
  onSelect,
}: {
  label: string;
  initial?: string;
  onSelect: (city: City) => void;
}) {
  const [q, setQ] = useState(initial ?? "");
  const [hits, setHits] = useState<City[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const listId = useId();
  // Skip the search when seeded with an `initial` display value or right after a pick.
  const skip = useRef(Boolean(initial));

  useEffect(() => {
    if (skip.current) {
      skip.current = false;
      return;
    }
    const query = q.trim();
    if (query.length < 2) {
      setHits([]);
      setLoading(false);
      setSearched(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      const res = await citiesApi.search(query);
      setLoading(false);
      setSearched(true);
      setHits(res.success && res.data ? res.data : []);
      setOpen(true);
      setActive(0);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  function choose(city: City) {
    skip.current = true;
    setQ(`${city.name}, ${city.country}`);
    setHits([]);
    setOpen(false);
    setSearched(false);
    onSelect(city);
  }

  const panel = open && q.trim().length >= 2 && (loading || searched);

  return (
    <div className="cac">
      <input
        className="cac__input"
        value={q}
        aria-label={label}
        aria-expanded={open}
        aria-controls={listId}
        autoCapitalize="none"
        autoComplete="off"
        placeholder="Search a city…"
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.trim().length >= 2 && setOpen(true)}
        onKeyDown={(e) => {
          if (!open || hits.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, hits.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const hit = hits[active];
            if (hit) choose(hit);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {panel && (
        <div className="cac__panel" id={listId}>
          {loading && <p className="cac__status">Searching…</p>}
          {!loading && hits.length === 0 && (
            <p className="cac__status">No matches for "{q.trim()}". Try another spelling.</p>
          )}
          {!loading && hits.length > 0 && (
            <ul className="cac__list">
              {hits.map((city, i) => (
                <li key={city.id}>
                  <button
                    type="button"
                    className={`cac__option${i === active ? " is-active" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(city);
                    }}
                  >
                    <span className="cac__flag" aria-hidden="true">
                      {flag(city.country_code)}
                    </span>
                    <span className="cac__city">{city.name}</span>
                    <span className="cac__country">{city.country}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
